import { useState } from "react";
import { type ThemeObject, SP, RADIUS, SHADOW, GLASS, TRANSITION, TYPE } from "../shared/theme.js";
import { supabase } from "../supabaseClient.js";

type ConsentKey = "analytics" | "marketing" | "data_sharing" | "portfolio_access";

interface LGPDConsentBannerProps {
  T: ThemeObject;
  userId: string;
  onComplete?: () => void;
}

export default function LGPDConsentBanner({ T, userId, onComplete }: LGPDConsentBannerProps) {
  const [consents, setConsents] = useState<Record<ConsentKey, boolean>>({
    analytics: false,
    marketing: false,
    data_sharing: false,
    portfolio_access: true,
  });
  const [loading, setLoading] = useState(false);

  const handleToggle = (key: ConsentKey) => {
    if (key === "portfolio_access") return;
    setConsents((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAcceptAll = async () => {
    setLoading(true);
    try {
      await saveConsents({ ...consents, analytics: true, marketing: true, data_sharing: true });
    } finally {
      setLoading(false);
    }
  };

  const handleOnlyNecessary = async () => {
    setLoading(true);
    try {
      await saveConsents({
        analytics: false,
        marketing: false,
        data_sharing: false,
        portfolio_access: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConsents = async (consentMap: Record<ConsentKey, boolean>) => {
    try {
      const records = Object.entries(consentMap).map(([type, granted]) => ({
        user_id: userId,
        consent_type: type,
        granted,
        ip_address: null,
        user_agent: navigator.userAgent,
        granted_at: new Date().toISOString(),
      }));

      const { error: err } = await supabase
        .from("consent_records")
        .upsert(records, { onConflict: "user_id,consent_type" });

      if (err) throw err;

      localStorage.setItem(
        "lgpd_consent",
        JSON.stringify({ timestamp: new Date().toISOString(), consents: consentMap })
      );

      onComplete?.();
    } catch (error: unknown) {
      console.error(
        "[LGPDConsentBanner] Erro ao salvar consentimento:",
        error instanceof Error ? error.message : error
      );
      localStorage.setItem(
        "lgpd_consent",
        JSON.stringify({ timestamp: new Date().toISOString(), consents: consentMap })
      );
      onComplete?.();
    }
  };

  const categories: Array<{ key: ConsentKey; label: string; desc: string }> = [
    { key: "analytics", label: "Análise de Uso", desc: "Entender como você usa o portal" },
    { key: "marketing", label: "Marketing", desc: "Comunicações e ofertas personalizadas" },
    {
      key: "data_sharing",
      label: "Compartilhamento de Dados",
      desc: "Compartilhamento com parceiros de investimento",
    },
    {
      key: "portfolio_access",
      label: "Acesso ao Portfólio",
      desc: "Essencial para acessar o portal",
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        padding: SP[4],
        background: `linear-gradient(180deg, transparent 0%, ${T.bg} 30%)`,
        backdropFilter: GLASS.dark.blur,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-end",
      }}
    >
      <div
        style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.lg,
          boxShadow: SHADOW.lg,
          padding: SP[6],
          maxWidth: 540,
          width: "100%",
          fontFamily: TYPE.fontSans,
        }}
      >
        <h2
          style={{
            fontSize: TYPE.xl,
            fontWeight: TYPE.bold,
            color: T.text,
            margin: `0 0 ${SP[2]}`,
          }}
        >
          Sua privacidade é importante
        </h2>
        <p
          style={{
            fontSize: TYPE.base,
            color: T.sub,
            margin: `0 0 ${SP[4]}`,
            lineHeight: TYPE.relaxed,
          }}
        >
          Personalizamos sua experiência com consentimento LGPD. Você pode mudar essas configurações
          a qualquer momento no seu perfil.
        </p>

        <div style={{ marginBottom: SP[6] }}>
          {categories.map(({ key, label, desc }) => (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: SP[3],
                padding: SP[3],
                marginBottom: SP[2],
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.md,
                cursor: key === "portfolio_access" ? "not-allowed" : "pointer",
                opacity: key === "portfolio_access" ? 0.7 : 1,
                transition: TRANSITION.normal,
              }}
              onClick={() => handleToggle(key)}
            >
              <input
                type="checkbox"
                checked={consents[key]}
                onChange={(e) => e.stopPropagation()}
                disabled={key === "portfolio_access"}
                style={{
                  width: 20,
                  height: 20,
                  marginTop: 2,
                  cursor: key === "portfolio_access" ? "not-allowed" : "pointer",
                  accentColor: T.accent,
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: TYPE.md,
                    fontWeight: TYPE.semibold,
                    color: T.text,
                    marginBottom: 2,
                  }}
                >
                  {label}
                  {key === "portfolio_access" && (
                    <span
                      style={{
                        fontSize: TYPE.xs,
                        fontWeight: TYPE.regular,
                        color: T.sub,
                        marginLeft: SP[2],
                      }}
                    >
                      (obrigatório)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: TYPE.sm, color: T.muted }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginBottom: SP[4],
            padding: SP[2],
            background: `${T.accent}15`,
            borderRadius: RADIUS.sm,
            borderLeft: `3px solid ${T.accent}`,
          }}
        >
          <a
            href="/privacy"
            style={{
              fontSize: TYPE.sm,
              color: T.accent,
              textDecoration: "none",
              fontWeight: TYPE.semibold,
            }}
          >
            📋 Ler política de privacidade completa →
          </a>
        </div>

        <div style={{ display: "flex", gap: SP[3], flexWrap: "wrap" }}>
          <button
            onClick={handleOnlyNecessary}
            disabled={loading}
            style={{
              flex: 1,
              minWidth: 120,
              padding: `${SP[3]} ${SP[4]}`,
              background: T.surface,
              color: T.text,
              border: `1.5px solid ${T.border}`,
              borderRadius: RADIUS.md,
              fontSize: TYPE.md,
              fontWeight: TYPE.semibold,
              cursor: loading ? "not-allowed" : "pointer",
              transition: TRANSITION.normal,
              opacity: loading ? 0.6 : 1,
            }}
          >
            Apenas Necessários
          </button>
          <button
            onClick={handleAcceptAll}
            disabled={loading}
            style={{
              flex: 1,
              minWidth: 120,
              padding: `${SP[3]} ${SP[4]}`,
              background: T.accent,
              color: T.accentText,
              border: "none",
              borderRadius: RADIUS.md,
              fontSize: TYPE.md,
              fontWeight: TYPE.semibold,
              cursor: loading ? "not-allowed" : "pointer",
              transition: TRANSITION.normal,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Salvando..." : "Aceitar Todos"}
          </button>
        </div>
      </div>
    </div>
  );
}
